import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { EmptyStateComponent } from '../../../@commons/ui-components/empty-state/empty-state.component';
import { StepListComponent, StepListItem } from '../../../@commons/ui-components/step-list/step-list.component';
import { SetupStep, SetupStepService } from './setup-step.service';
import { SproutIconComponent } from '../../../@icons/sprout-icon.component';
import { PlusIconComponent } from '../../../@icons/plus-icon.component';
import routesConstants from '../../../@commons/constants/routes.constants';

/** Les trois étapes du premier lancement, dans l'ordre. */
export type FirstLaunchStep = 'commune' | 'zone' | 'plants';
const STEPS: FirstLaunchStep[] = ['commune', 'zone', 'plants'];

/** Où mène chaque étape : l'adresse du compte, l'assistant de zone, l'ajout d'une plante. */
const ROUTES: Record<FirstLaunchStep, { path: string; queryParams?: Record<string, string> }> = {
  commune: { path: routesConstants.PROFILE },
  zone: { path: routesConstants.GARDEN_LIST, queryParams: { addZone: '1' } },
  plants: { path: routesConstants.GARDEN_ADDPLANT },
};

/**
 * Le premier lancement (`vw-first-launch`) : compte créé, potager pas encore
 * configuré. Le message du jour devient un accueil : la pastille « Bienvenue »,
 * « On installe ton potager ? », Willy petit à droite, trois étapes
 * (`vw-step-list` dans `vw-empty-state`), puis l'étape discrète des alertes ou
 * de l'app, un seul bouton pour l'étape suivante (« + » quand il ajoute une
 * zone ou des plantes) et « Plus tard ». La commune
 * connue remplace l'explication de la première étape (« Liège »).
 * Usage : `<vw-first-launch [done]="{ commune: true, zone: false, plants: false }" [commune]="'Liège'" [setup]="…" (later)="…" />`
 */
@Component({
  selector: 'vw-first-launch',
  standalone: true,
  imports: [TranslatePipe, EmptyStateComponent, StepListComponent, SproutIconComponent, PlusIconComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <section class="rounded-sheet bg-gradient-to-b from-vw-surface-tint to-vw-bg ring-1 ring-inset ring-vw-line" data-test="first-launch">
      <vw-empty-state align="start" willy="hi" titleKey="home.welcome.title" messageKey="home.welcome.message"
        [actionKey]="next ? 'home.welcome.action.' + next : null" actionLook="primary" secondaryActionKey="home.welcome.later"
        (action)="open(next)" (secondaryAction)="later.emit()">
        <p emptyStateKicker class="flex items-center gap-1.5 text-body-s font-semibold text-vw-primary-ink" data-test="first-launch-kicker">
          <vw-sprout-icon class="h-[18px] w-[18px]" aria-hidden="true"></vw-sprout-icon>
          {{ 'home.welcome.kicker' | translate }}
        </p>
        <vw-step-list class="w-full" [steps]="steps" (stepClick)="onStep($event)"></vw-step-list>
        @if (next === 'zone' || next === 'plants') {
          <vw-plus-icon emptyStateActionIcon class="block h-5 w-5 shrink-0 [&>svg]:h-full [&>svg]:w-full" aria-hidden="true"></vw-plus-icon>
        }
      </vw-empty-state>
    </section>
  `,
})
export class FirstLaunchComponent {
  /** Ce qui est déjà fait. */
  @Input() done: Record<FirstLaunchStep, boolean> = { commune: false, zone: false, plants: false };
  /** La commune du jardin, quand elle est connue. */
  @Input() commune: string | null = null;
  /** L'étape en plus (alertes ou app) ; aucune quand il n'y a rien à proposer. */
  @Input() setup: SetupStep | null = null;
  /** « Plus tard » : l'accueil le range pour aujourd'hui. */
  @Output() later = new EventEmitter<void>();

  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly setupStep = inject(SetupStepService);

  /** L'étape suivante : la première des trois qui n'est pas faite. */
  get next(): FirstLaunchStep | null {
    return STEPS.find((step) => !this.done[step]) ?? null;
  }

  get steps(): StepListItem[] {
    const main = STEPS.map((step): StepListItem => ({
      label: this.translate.instant(`home.welcome.steps.${step}.label`),
      detail: step === 'commune' && this.done.commune && this.commune ? this.commune : this.translate.instant(`home.welcome.steps.${step}.detail`),
      state: this.done[step] ? 'done' : step === this.next ? 'next' : 'todo',
    }));
    if (!this.setup) return main;
    return [
      ...main,
      {
        label: this.translate.instant(`home.welcome.steps.${this.setup.kind}.label`),
        detail: this.translate.instant(`home.welcome.steps.${this.setup.kind}.detail`),
        state: this.setup.done ? 'done' : 'todo',
        discreet: true,
      },
    ];
  }

  onStep(index: number): void {
    if (index < STEPS.length) {
      this.open(STEPS[index]);
    } else if (this.setup && !this.setup.done) {
      this.setupStep.open(this.setup.kind);
    }
  }

  open(step: FirstLaunchStep | null): void {
    if (!step) return;
    const route = ROUTES[step];
    void this.router.navigate([route.path], route.queryParams ? { queryParams: route.queryParams } : {});
  }
}
